import {
	Breadcrumb,
	Button,
	Card,
	Empty,
	Modal,
	Space,
	TablePaginationConfig,
} from 'antd';
import { ReceivingFiler } from './ReceivingFilter.tsx';
import { useEffect, useState, useRef, useMemo } from 'react';
import type { HTMLAttributes } from 'react';
import { ReceivingForm } from './ReceivingForm.tsx';
import { useGetMaterialQuery } from '../../../redux/api/material.ts';
import { useQueryParameters } from '../../../hooks';
import {
	MaterialObject
} from '../../../interfaces/Material.Interface.ts';
import dayjs from 'dayjs';

import { FilterValue } from 'antd/es/table/interface';
import { useNavigate } from 'react-router';
import PaginationComponent from '../../../components/Footers/Pagination.tsx';
import CustomResponsiveTable from '../../../components/CustomResponsiveTable';
import { normalizeStatusDelivered } from '../../../utils';
import { useGetBuildingsQuery } from '../../../redux/api/location';
import BuildingTag from '../../../components/BuildingTag';
import { useHasMultipleBuildings } from '../../../hooks/useHasMultipleBuildings';

interface MaterialQuery {
	page?: number;
	search?: string;
	ordering?: string;
	module?: string;
	status?: string;
}

interface Props {
	is_tablet?: boolean;
	filter_status?: string;
	building_id?: string;
}

export const ListMaterialReceiving = (props: Props) => {
	const navigate = useNavigate();
	const queryParams = useQueryParameters();
	const { data: buildings = [] } = useGetBuildingsQuery();
	const showBuildingColumn = useHasMultipleBuildings();
	const tabletBuildingName = useMemo(() => {
		if (!props.building_id) return null;
		return buildings.find((b) => b.id === props.building_id)?.name ?? null;
	}, [props.building_id, buildings]);
	const currentPageUrl = Number(queryParams.get('page')) || 1;
	const searchUrl = queryParams.get('search') || '';
	const ordering = queryParams.get('ordering') || '';

	const [showModalReceiving, setShowModalReceiving] = useState(false);
	const [materials, setMaterials] = useState<MaterialObject[]>([]);
	const [materialSelected, setMaterialSelected] =
		useState<MaterialObject | null>(null);
	const [totalMaterials, setTotalMaterials] = useState<number>(0);
	const filterStatus = props.filter_status;

	const allowedStatuses = ["Partially Received", "Awaiting Receive"];

	const prevFilterRef = useRef<string | undefined>(filterStatus);
	const statusChanged = filterStatus !== prevFilterRef.current;

	const pageForQuery = statusChanged ? 1 : currentPageUrl;

	const materialItemsQuery: MaterialQuery = {
		module: 'material_received',
		page: pageForQuery,
		...(searchUrl ? { search: searchUrl } : {}),
		...(ordering ? { ordering } : {}),
		...(filterStatus ? { status: filterStatus } : {}),
		...(props.building_id ? { building: props.building_id } : {}),
	};

	const { data, isLoading, isFetching, refetch } = useGetMaterialQuery(materialItemsQuery);
	const [pageReady, setPageReady] = useState(false);


	const refetchData = () => {
		refetch();
	};

	useEffect(() => {
		if (data) {
			setTotalMaterials(data.count || 0);
			setMaterials(data.results || []);
			// Don't clear materialSelected while the receiving modal is open —
			// doing so unmounts ReceivingForm and destroys the location summary modal
			if (!showModalReceiving) {
				setMaterialSelected(null);
			}
			setPageReady(true);
		}
	}, [data]);

	useEffect(() => {
		if (statusChanged) {
			queryParams.set('page', '1');
			navigate({ search: queryParams.toString() }, { replace: true });
			prevFilterRef.current = filterStatus;
		}
	}, [statusChanged, filterStatus, navigate]);

	const handleOpenModal = (id: string) => {
		// Todo: make a query to get the material order item details
		const material = materials.find((item: MaterialObject) => item.id === id);
		if (material) {
			setMaterialSelected(material);
		}
		setShowModalReceiving(true);
	};

	const handleCancelReceiving = () => {
		setShowModalReceiving(false);
		setMaterialSelected(null);
	};

	const handleTableChange = (
		_pagination: TablePaginationConfig,
		_filters: Record<string, FilterValue | null>,
		sorter: { column: string; columnKey: string; order: string },
		extra: { action: string }
	) => {
		console.log(sorter);
		if (extra.action === 'sort') {
			const orderType = sorter.order;
			const column =
				orderType === 'ascend' || orderType === undefined
					? sorter.columnKey
					: '-' + sorter.columnKey;
			if (sorter.column !== undefined) {
				queryParams.set('ordering', column);
			} else {
				queryParams.delete('ordering');
			}
			navigate({ search: queryParams.toString() });
		}
	};

	const tableColumns = [
		{
			title: 'Material Order',
			width: '2.2%',
			key: 'material_order__vendor_order_number',
			render: (record: MaterialObject) => {
				return <>
					<a onClick={() => {
						navigate(`/material/orders/${record.material_order.id}`);
					}}>
						{record.material_order?.vendor?.display_name ?? record.material_order?.id ?? 'N/A'}
					</a>
				</>

			},
			sorter: true,
		},
		{
			title: 'PO Number',
			width: '2%',
			key: 'material_order__purchase_order_number',
			render: (record: MaterialObject) => {
				return record.material_order?.purchase_order_name || '';
			},
			sorter: (a: MaterialObject, b: MaterialObject) => {
				const valueA = a.material_order?.purchase_order_number || '';
				const valueB = b.material_order?.purchase_order_number || '';

				if (valueA === '' && valueB !== '') return 1;
				if (valueA !== '' && valueB === '') return -1;

				return valueA.localeCompare(valueB);
			},
		},
		{
			title: 'Material',
			width: '3%',
			render: (record: MaterialObject) => (
				<div className="flex flex-col">
					<span>{record.type?.display_name || 'N/A'}</span>
					<span>{record.shape?.display_name || 'N/A'}</span>
					<span>{record?.display_size || 'N/A'}</span>
				</div>
			),
		},
		{
			title: 'Material Length',
			width: '2%',
			key: 'length',
			render: (record: MaterialObject) => {
				return `${record.length} In.`;
			},
			sorter: true,
		},
		{
			title: 'Ordered / Delivered',
			width: '2%',
			key: 'quantity_ordered',
			render: (record: MaterialObject) => {
				return `${record.quantity_ordered} / ${record.quantity_total_received}`;
			},
			sorter: true,
		},
		{
			title: 'Promised Delivery Date',
			width: '3%',
			key: 'promised_delivery_date',
			render: (record: MaterialObject) => {
				if (!record.promised_delivery_date) {
					return 'N/A';
				} else {
					return (
						dayjs(record.promised_delivery_date).format('MMMM DD, YYYY') ||
						'N/A'
					);
				}
			},
			sorter: (a: MaterialObject, b: MaterialObject) => {
				const dateA = a.promised_delivery_date || '';
				const dateB = b.promised_delivery_date || '';
				return dateA.localeCompare(dateB);
			},
		},
		...(showBuildingColumn
			? [
					{
						title: 'Building',
						width: '2%',
						key: 'building',
						render: (record: any) =>
							record.building_name ?? '—',
					},
				]
			: []),
		{
			title: 'Status',
			width: '3%',
			key: 'status',
			dataIndex: 'status',
			sorter: true,
			render: (status: string) => normalizeStatusDelivered(status),
		},
		{
			title: 'Actions',
			width: '2%',
			key: 'actions',
			render: (_: string, record: any) => (
				<div className="flex flex-row">
					<Button
						disabled={!allowedStatuses
							.includes(normalizeStatusDelivered(record.status))}
						type="primary"
						className="mr-1"
						onClick={() => handleOpenModal(record.id)}
					>
						Receive
					</Button>
				</div>
			),
		},
	];

	const tableFooter = () => {
		return (
			<div className="flex justify-between items-center">
				<div className="ml-auto">
					<PaginationComponent pageSize={20} total={totalMaterials} />
				</div>
			</div>
		);
	};

	const emptyTable = () => {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={
					<span style={{ color: '#000' }}>
						There are currently no materials orders to be displayed.
					</span>
				}
			/>
		);
	};

	return (
		<main
			data-testid={props.is_tablet ? undefined : 'page-fastlane-material-receiving'}
			data-ready={props.is_tablet ? undefined : pageReady ? 'true' : 'false'}
		>
			{!props.is_tablet && (
				<>
					<div className="flex flex-column justify-between py-4">
						<h1 className="text-2xl font-bold m-0">Material Receiving</h1>
					</div>

					<Breadcrumb
						style={{ marginBottom: '1.5rem', marginTop: '.5rem' }}
						items={[
							{
								title: 'Home',
							},
							{
								title: 'Material Receiving',
							},
						]}
					/>

					<div style={{ marginBottom: '1rem' }}>
						<ReceivingFiler />
					</div>
				</>
			)}

			<Card className="shadow-sm" bodyStyle={{ padding: 0 }}>
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<CustomResponsiveTable
						columns={tableColumns}
						data={materials}
						pagination={false}
						loading={isLoading}
						rowKey="id"
						onRow={(record) => ({
							'data-testid': 'fastlane-material-receiving-row',
							'data-material-id': record.id,
							'data-material-order-id': record.material_order?.id || '',
						} as HTMLAttributes<HTMLElement>)}
						footer={tableFooter}
						// eslint-disable-next-line
						//@ts-ignore
						onChange={handleTableChange}
						emptyTable={emptyTable}
						tableMaxWidth={1200}
						excludeWidthColumn={true}
					/>
				</div>
			</Card>

			<Modal
				title={
				<Space>
					<span>Receive Material</span>
					<BuildingTag name={tabletBuildingName ?? materialSelected?.building_name} />
				</Space>
				}
				centered={true}
				open={showModalReceiving}
				width={800}
				footer={false}
				closable={false}
				destroyOnClose
			>
				{materialSelected && (
				<ReceivingForm
					refetchData={refetchData}
					material={materialSelected}
					onCancel={handleCancelReceiving}
					building_id={props.building_id}
				/>
				)}
			</Modal>
		</main>
	);
};
